import { h } from '@/lib/dom';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toast';
import { data } from '@/lib/data';
import { formatRelative } from '@/lib/format';
import { currentSession } from '@/lib/session';
import type { RouteContext } from '@/router';
import { navigate } from '@/router';
import type { WhoopStatus } from '@/api/whoop';
import { pageLayout } from './layout';

const WHOOP_AUTHORIZE_URL = '/api/v1/integrations/whoop/authorize';

export async function renderSettings(ctx: RouteContext): Promise<Node> {
  const user = currentSession();
  if (!user) {
    navigate('/login', { replace: true });
    return h('div');
  }
  if (user.role !== 'owner') {
    // Doctor has no settings to manage; bounce home.
    navigate('/', { replace: true });
    return h('div');
  }

  handleCallbackToasts(ctx.query);

  const whoopRoot = h('div', { id: 'settings-whoop' }, [
    Skeleton({ variant: 'rect', height: '120px' }),
  ]);

  const root = pageLayout({
    user,
    active: 'settings',
    children: [
      h('div', { class: 'page-header' }, [
        h('div', null, [
          h('h1', { class: 'page-title' }, 'Настройки'),
          h('p', { class: 'page-subtitle' },
            'Управление подключёнными интеграциями.'),
        ]),
      ]),
      whoopRoot,
    ],
  });

  void (async () => {
    try {
      const status = await data.getWhoopStatus();
      whoopRoot.innerHTML = '';
      whoopRoot.appendChild(whoopCard(status, async () => {
        whoopRoot.innerHTML = '';
        const fresh = await data.getWhoopStatus();
        whoopRoot.appendChild(whoopCard(fresh, async () => {
          whoopRoot.innerHTML = '';
          const s2 = await data.getWhoopStatus();
          whoopRoot.appendChild(whoopCard(s2, () => Promise.resolve()));
        }));
      }));
      document.dispatchEvent(new Event('route:settled'));
    } catch (err) {
      console.error(err);
      whoopRoot.innerHTML = '';
      whoopRoot.appendChild(Card({
        title: 'WHOOP',
        children: h('p', { role: 'alert' },
          'Не удалось загрузить статус. Попробуйте обновить страницу.'),
      }));
    }
  })();

  return root;
}

function handleCallbackToasts(query: URLSearchParams): void {
  const whoop = query.get('whoop');
  if (!whoop) return;
  if (whoop === 'connected') {
    toast('WHOOP подключён', { type: 'success' });
  } else if (whoop === 'error') {
    const reason = query.get('reason') ?? 'unknown';
    toast(`Ошибка подключения WHOOP: ${reason}`, { type: 'error', duration: 6000 });
  }
  const url = new URL(window.location.href);
  url.searchParams.delete('whoop');
  url.searchParams.delete('reason');
  window.history.replaceState({}, '', url.pathname + (url.search || ''));
}

function statusDot(color: string): HTMLElement {
  return h('span', {
    style: `display:inline-block; width:8px; height:8px; border-radius:50%; background:${color}; margin-right: var(--space-2); flex-shrink:0;`,
    'aria-hidden': 'true',
  });
}

function whoopCard(status: WhoopStatus, refresh: () => Promise<void>): HTMLElement {
  const actions = h('div', {
    style: 'display:flex; flex-direction:column; align-items:flex-end; gap:var(--space-2);',
  });

  const infoLines: Node[] = [];

  if (!status.oauth_configured) {
    const dotEl = statusDot('var(--text-disabled)');
    infoLines.push(h('p', { style: 'font-size:var(--text-sm); display:flex; align-items:center;' }, [
      dotEl,
      h('span', { style: 'color:var(--text-tertiary);' }, 'Не подключено'),
    ]));
    infoLines.push(h('p', {
      class: 'text-tertiary',
      style: 'font-size:var(--text-xs); margin-top:var(--space-1);',
    }, 'OAuth не настроен: заполните WHOOP_CLIENT_ID / WHOOP_CLIENT_SECRET / WHOOP_REDIRECT_URI в .env и перезапустите backend.'));
    actions.appendChild(Button({
      label: 'Подключить WHOOP',
      variant: 'primary',
      disabled: true,
    }));
  } else if (status.connected) {
    const rel = status.connected_at ? formatRelative(status.connected_at) : null;
    const dotEl = statusDot('var(--status-norm-fg)');
    infoLines.push(h('p', {
      style: 'font-size:var(--text-sm); display:flex; align-items:center; color:var(--status-norm-fg);',
    }, [
      dotEl,
      'Подключено',
    ]));
    if (rel) {
      infoLines.push(h('p', {
        class: 'text-tertiary',
        style: 'font-size:var(--text-xs); margin-top:var(--space-1);',
      }, `обновлено ${rel}`));
    }
    if (status.scope) {
      const email = extractEmail(status.scope);
      if (email) {
        infoLines.push(h('p', {
          class: 'text-secondary',
          style: 'font-size:var(--text-sm); margin-top:var(--space-2);',
        }, email));
      }
    }

    const reconnectBtn = h('a', {
      href: WHOOP_AUTHORIZE_URL,
      class: 'btn btn-secondary',
      style: 'width:160px; text-align:center;',
      'data-external': 'true',
    }, 'Переподключить');
    const disconnectBtn = Button({
      label: 'Отключить',
      variant: 'danger',
      onClick: async () => {
        if (!window.confirm('Отключить WHOOP и удалить сохранённые токены?')) return;
        (disconnectBtn as HTMLButtonElement).disabled = true;
        try {
          await data.disconnectWhoop();
          toast('WHOOP отключён', { type: 'info' });
          await refresh();
        } catch (err) {
          (disconnectBtn as HTMLButtonElement).disabled = false;
          console.error(err);
          toast('Не удалось отключить WHOOP', { type: 'error' });
        }
      },
    });
    actions.appendChild(reconnectBtn);
    actions.appendChild(disconnectBtn);
  } else {
    const dotEl = statusDot('var(--text-disabled)');
    infoLines.push(h('p', {
      style: 'font-size:var(--text-sm); display:flex; align-items:center; color:var(--text-tertiary);',
    }, [
      dotEl,
      'Не подключено',
    ]));
    infoLines.push(h('p', {
      style: 'font-size:var(--text-sm); margin-top:var(--space-2);',
    }, 'Нажмите «Подключить WHOOP» — вас перекинет на страницу авторизации WHOOP, после чего вернёт обратно.'));
    actions.appendChild(h('a', {
      href: WHOOP_AUTHORIZE_URL,
      class: 'btn btn-primary',
      style: 'width:160px; text-align:center;',
      'data-external': 'true',
    }, 'Подключить WHOOP'));
  }

  const separator = h('div', {
    style: 'border-top:1px solid var(--border-default); margin:var(--space-5) 0;',
  });

  const inner = h('div', {
    style: 'display:grid; grid-template-columns:1fr auto; gap:var(--space-4); align-items:start;',
  }, [
    h('div', null, [
      h('div', {
        style: 'display:flex; align-items:center; gap:var(--space-2); margin-bottom:var(--space-3);',
      }, [
        h('span', { style: 'font-size:18px; line-height:1;' }, '⌚'),
        h('h2', {
          style: 'font-size:var(--text-lg); font-weight:var(--fw-semi); margin:0;',
        }, 'WHOOP'),
      ]),
      h('p', {
        class: 'text-secondary',
        style: 'font-size:var(--text-sm); margin-bottom:var(--space-3);',
      }, 'Тренировки, восстановление, HRV — через WHOOP Developer API.'),
      ...infoLines,
    ]),
    actions,
  ]);

  const card = Card({
    title: '',
    children: [inner, separator],
  });

  // Override card to remove the empty title bar visual gap.
  card.style.maxWidth = '560px';
  card.setAttribute('data-integration', 'whoop');

  // Remove the title element since we rendered title inside the card body.
  const titleEl = card.querySelector('.card-title');
  if (titleEl) titleEl.remove();

  return card;
}

function extractEmail(scope: string): string | null {
  // scope sometimes contains email info; this is a best-effort fallback.
  // Return null if nothing looks like email.
  const match = scope.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
  return match ? match[0] : null;
}
